import { useQuery } from '@apollo/client/react';
import { useMemo } from 'react';
import type { Options } from 'react-select';
import { graphql } from '../../gql';
import type { AnimalTypesQueryQuery } from '../../gql/graphql';
import {
  type Animal,
  type DropdownOption,
  transformAnimalOption,
} from '../utils/optionTransformers';
import type { QueryResult } from './types/QueryResult';

export const animalTypesQuery = graphql(/*GraphQL*/ `
  query AnimalTypesQuery {
    viewer {
      animalTypes {
        items {
          label
          value
        }
      }
    }
  }
`);

export const animalDropdownNoOption: DropdownOption<Animal> = {
  data: { label: 'No', value: 'null' },
  label: 'No',
  value: 'null',
};

export const animalDropdownYesOption: DropdownOption<Animal> = {
  data: { label: 'Yes', value: 'yes' },
  label: 'Yes',
  value: 'yes',
};

function transformOptions(
  data: AnimalTypesQueryQuery | undefined,
  options?: { includeNoOption?: boolean; includeYesOption?: boolean },
): Options<DropdownOption<Animal>> {
  if (data?.viewer?.animalTypes?.items) {
    const dropdownOptions: DropdownOption<Animal>[] = [];
    if (options?.includeYesOption) {
      dropdownOptions.push(animalDropdownYesOption);
    }
    if (options?.includeNoOption) {
      dropdownOptions.push(animalDropdownNoOption);
    }
    dropdownOptions.push(...data.viewer.animalTypes.items.map(transformAnimalOption));
    return dropdownOptions;
  } else {
    return [];
  }
}

export const useAnimalTypesOptions = (options?: {
  includeNoOption?: boolean;
  includeYesOption?: boolean;
  skipQuery?: boolean;
}): QueryResult<Options<DropdownOption<Animal>>> => {
  const { data, loading, error } = useQuery(animalTypesQuery, {
    fetchPolicy: 'cache-first',
    skip: options?.skipQuery,
  });

  return useMemo(
    () => [
      transformOptions(data, {
        includeNoOption: options?.includeNoOption,
        includeYesOption: options?.includeYesOption,
      }),
      loading,
      error,
    ],
    [data, error, options?.includeNoOption, options?.includeYesOption, loading],
  );
};
